import type { StepType } from './types';

// Map of allowed CHILDREN per parent step type (user-addable types only —
// virtual children like `channel` and `band` are synced from parent params,
// never added through the "Add step" menu).
const ALLOWED_CHILDREN: Record<StepType, StepType[]> = {
  input: ['greyscale', 'color_separate', 'gradient_sep'],
  greyscale: ['gradient_sep', 'halftone'],
  color_separate: [], // children synced from `channels`
  channel: ['greyscale', 'halftone'],
  gradient_sep: [], // children synced from `bands`
  band: ['halftone'],
  halftone: [],
};

// Step types whose direct output is a 1-bit binary mask (printable layer).
// `channel` produces binary masks via winner-take-all classification, so it
// is leaf-producing in v1; users may still attach a halftone child for a
// stylized look on the binary pattern.
const LEAF_PRODUCING: Record<StepType, boolean> = {
  input: false,
  greyscale: false,
  color_separate: false,
  channel: true,
  gradient_sep: false,
  band: true,
  halftone: true,
};

// Step types limited to a single child. Only `input` is single-child in v1
// (one downstream pipeline rooted at the upload). `greyscale` and `channel`
// allow multiple children so users can experiment with multiple halftone
// variants of the same upstream buffer.
const SINGLE_CHILD: Record<StepType, boolean> = {
  input: true,
  greyscale: false,
  color_separate: false,
  channel: false,
  gradient_sep: false,
  band: false,
  halftone: false,
};

export function allowedChildren(parent: StepType): StepType[] {
  return ALLOWED_CHILDREN[parent];
}

export function isLeafProducing(type: StepType): boolean {
  return LEAF_PRODUCING[type];
}

export function singleChild(type: StepType): boolean {
  return SINGLE_CHILD[type];
}

export function isVirtualNode(type: StepType): boolean {
  return type === 'channel' || type === 'band';
}
